import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import Layout from '../../components/Layout';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import { useAuth } from '../../context/AuthContext';
import { apiService } from '../../services/api';
import './UserProfilePage.css';

import { getServerErrorMessage } from '../../utils/errorUtils';
const UserProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [showNotifications, setShowNotifications] = useState(true);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({});
  const [passwordMessage, setPasswordMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [submittingPassword, setSubmittingPassword] = useState(false);

  const validatePasswordForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!passwordData.currentPassword.trim()) {
      errors.currentPassword = t('userProfile.currentPasswordRequired');
    }

    if (!passwordData.newPassword.trim()) {
      errors.newPassword = t('userProfile.newPasswordRequired');
    } else if (passwordData.newPassword.length < 8) {
      errors.newPassword = t('userProfile.passwordMinLength');
    }

    if (!passwordData.confirmPassword.trim()) {
      errors.confirmPassword = t('userProfile.confirmPasswordRequired');
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = t('userProfile.passwordMismatch');
    }

    if (passwordData.currentPassword === passwordData.newPassword) {
      errors.newPassword = t('userProfile.passwordSameAsOld');
    }

    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
    if (passwordErrors[name]) {
      setPasswordErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordMessage(null);

    if (!validatePasswordForm()) {
      return;
    }

    setSubmittingPassword(true);
    try {
      await apiService.post('/auth/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });

      setPasswordMessage({
        type: 'success',
        text: t('userProfile.passwordChangeSuccess'),
      });

      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });

      setTimeout(() => {
        setShowPasswordForm(false);
        setPasswordMessage(null);
      }, 2000);
    } catch (error: any) {
      setPasswordMessage({
        type: 'error',
        text: getServerErrorMessage(error) || t('userProfile.passwordChangeError'),
      });
    } finally {
      setSubmittingPassword(false);
    }
  };

  if (!user) {
    return (
      <Layout>
        <div style={{ padding: 32 }}>
          <div className="ims-alert ims-alert-error">{t('common.userNotFound')}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="user-profile-page">
        {/* ── Hero ── */}
        <div className="ims-hero">
          <div className="ims-hero-nav">
            <button className="ims-hero-back" onClick={() => navigate('/dashboard')}>
              <ArrowLeft size={16} strokeWidth={2} />
              {t('navigation.backToDashboard')}
            </button>
          </div>
          <div className="ims-hero-content">
            <div className="profile-header-avatar avatar avatar-lg avatar-primary">
              {user.firstName?.[0]}{user.lastName?.[0]}
            </div>
            <div>
              <h1 className="ims-hero-title">{user.firstName} {user.lastName}</h1>
              <p className="ims-hero-subtitle">{user.roles?.map(r => r.name).join(', ') || 'User'}</p>
            </div>
          </div>
        </div>

        {/* ── Profile Content ── */}
        <div className="profile-container">
          <div className="profile-card">
            <div className="profile-section">
              <h2 className="profile-section-title">{t('userProfile.accountInformation')}</h2>

              <div className="profile-info-grid">
                <div className="profile-info-item">
                  <label className="profile-info-label">{t('userProfile.firstName')}</label>
                  <div className="profile-info-value">{user.firstName}</div>
                </div>

                <div className="profile-info-item">
                  <label className="profile-info-label">{t('userProfile.lastName')}</label>
                  <div className="profile-info-value">{user.lastName}</div>
                </div>

                <div className="profile-info-item">
                  <label className="profile-info-label">{t('userProfile.email')}</label>
                  <div className="profile-info-value">{user.email}</div>
                </div>

                <div className="profile-info-item">
                  <label className="profile-info-label">{t('userProfile.roles')}</label>
                  <div className="profile-info-value">
                    {user.roles?.map((role, index) => (
                      <span key={role.id} className="ims-badge ims-badge-primary">
                        {role.name}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* ── Language Preferences ── */}
            <div className="profile-section profile-section-language">
              <h2 className="profile-section-title">{t('userProfile.preferences')}</h2>

              <div className="profile-setting-group">
                <div className="profile-setting-label">
                  <label>{t('userProfile.language')}</label>
                  <p className="profile-setting-description">{t('userProfile.languageDescription')}</p>
                </div>
                <div className="profile-setting-control">
                  <LanguageSwitcher />
                </div>
              </div>

              <div className="profile-setting-group">
                <div className="profile-setting-label">
                  <label>{t('userProfile.notifications')}</label>
                  <p className="profile-setting-description">{t('userProfile.notificationsDescription')}</p>
                </div>
                <div className="profile-setting-control">
                  <label className="ims-checkbox">
                    <input
                      type="checkbox"
                      checked={showNotifications}
                      onChange={(e) => setShowNotifications(e.target.checked)}
                    />
                    <span>{t('userProfile.enableNotifications')}</span>
                  </label>
                </div>
              </div>
            </div>

            {/* ── Security ── */}
            <div className="profile-section profile-section-security">
              <h2 className="profile-section-title">{t('userProfile.security')}</h2>

              {!showPasswordForm ? (
                <div className="profile-setting-group">
                  <div className="profile-setting-label">
                    <label>{t('userProfile.password')}</label>
                    <p className="profile-setting-description">{t('userProfile.passwordDescription')}</p>
                  </div>
                  <div className="profile-setting-control">
                    <button
                      className="ims-btn ims-btn-primary"
                      onClick={() => setShowPasswordForm(true)}
                    >
                      {t('userProfile.changePassword')}
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handlePasswordSubmit} className="password-form">
                  {passwordMessage && (
                    <div className={`ims-alert ims-alert-${passwordMessage.type}`}>
                      {passwordMessage.text}
                    </div>
                  )}

                  <div className="form-group">
                    <label htmlFor="currentPassword" className="form-label">
                      {t('userProfile.currentPassword')}
                      <span className="form-required">*</span>
                    </label>
                    <input
                      type="password"
                      id="currentPassword"
                      name="currentPassword"
                      className={`form-control ${passwordErrors.currentPassword ? 'is-invalid' : ''}`}
                      value={passwordData.currentPassword}
                      onChange={handlePasswordChange}
                      placeholder={t('userProfile.enterCurrentPassword')}
                      disabled={submittingPassword}
                    />
                    {passwordErrors.currentPassword && (
                      <div className="form-error">{passwordErrors.currentPassword}</div>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="newPassword" className="form-label">
                      {t('userProfile.newPassword')}
                      <span className="form-required">*</span>
                    </label>
                    <input
                      type="password"
                      id="newPassword"
                      name="newPassword"
                      className={`form-control ${passwordErrors.newPassword ? 'is-invalid' : ''}`}
                      value={passwordData.newPassword}
                      onChange={handlePasswordChange}
                      placeholder={t('userProfile.enterNewPassword')}
                      disabled={submittingPassword}
                    />
                    {passwordErrors.newPassword && (
                      <div className="form-error">{passwordErrors.newPassword}</div>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="confirmPassword" className="form-label">
                      {t('userProfile.confirmPassword')}
                      <span className="form-required">*</span>
                    </label>
                    <input
                      type="password"
                      id="confirmPassword"
                      name="confirmPassword"
                      className={`form-control ${passwordErrors.confirmPassword ? 'is-invalid' : ''}`}
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordChange}
                      placeholder={t('userProfile.confirmNewPassword')}
                      disabled={submittingPassword}
                    />
                    {passwordErrors.confirmPassword && (
                      <div className="form-error">{passwordErrors.confirmPassword}</div>
                    )}
                  </div>

                  <div className="form-actions">
                    <button
                      type="submit"
                      className="btn-save"
                      disabled={submittingPassword}
                    >
                      {submittingPassword ? t('common.saving') : t('userProfile.updatePassword')}
                    </button>
                    <button
                      type="button"
                      className="ims-btn ims-btn-outline"
                      onClick={() => {
                        setShowPasswordForm(false);
                        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                        setPasswordErrors({});
                        setPasswordMessage(null);
                      }}
                      disabled={submittingPassword}
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default UserProfilePage;
